import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import EstimateService from 'services/EstimateService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import 'date-fns';
import CustomerService from 'services/CustomerService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SelectFilterStyle, ROUTES, PRINT_TYPES } from 'const';
import { ShopContext } from 'Context/ShopContext';
import { BillFormatContext } from 'Context/BillFormatContext';
import { AlertMessagesContext } from 'react-alert-messages';
import { filterEndDate } from 'components/utils/FilterEndDate';
import ShopsHelper from 'helpers/ShopsHelper';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { TerminalContext } from 'Context/TerminalContext';
import { useTranslation } from 'react-i18next';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import { Grid } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useReportPrint } from 'hooks/useReportPrint';
import EstimateHelper from 'helpers/EstimateHelper';
import PrintDocument from 'components/utils/PrintDocument/index';

function EstimateReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const history = useHistory();

  const { shop } = useContext(ShopContext);
  const { getBillFormat } = useContext(BillFormatContext);
  const { terminals: terminalsFromContext } = useContext(TerminalContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [sorting, setSorting] = useState('default');
  const [filterStatus, setFilterStatus] = useState('all');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting,
    filterStatus,
  });
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [estimateToPrint, setEstimateToPrint] = useState(null);
  const [billFormatToPrint, setBillFormatToPrint] = useState(null);
  const [estimateTotalsToPrint, setEstimateTotalsToPrint] = useState(null);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const terminalMap = useMemo(
    () => Object.fromEntries(terminalsFromContext.map((terminal) => [terminal.id, terminal.name])),
    [terminalsFromContext]
  );
  const customerMap = useMemo(
    () =>
      Object.fromEntries((Array.isArray(customers) ? customers : []).map((customer) => [customer.id, customer.name])),
    [customers]
  );
  const salesPersonMap = useMemo(
    () => Object.fromEntries(salesPersons.map((person) => [person.id, person.name])),
    [salesPersons]
  );

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      sorting !== prevFilterData.sorting ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson ||
      filterStatus !== prevFilterData.filterStatus ||
      filterCustomer !== prevFilterData.filterCustomer
    );
  }, [
    filterFromDate,
    filterToDate,
    sorting,
    filterTerminal,
    filterSalesperson,
    filterStatus,
    filterCustomer,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.sorting,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.filterStatus,
    prevFilterData.filterCustomer,
  ]);

  const getCustomers = useCallback(async () => {
    try {
      const res = await CustomerService.getCustomers();
      setCustomers(res);
    } catch (err) {}
  }, []);

  const getSalespersons = useCallback(async () => {
    try {
      const res = await SalespersonsService.getSalesPersons();
      setSalesPersons(res);
    } catch (err) {}
  }, []);

  const loadEstimateReports = useCallback(
    async (startInput, endInput) => {
      const start = startInput instanceof Date ? startInput : filterFromDate;
      const end = endInput instanceof Date ? endInput : filterToDate;

      setLoadingIndicator(true);
      try {
        const params = {
          filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start).toISOString(),
          filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end).toISOString(),
          terminal_ids: filterTerminal.length ? filterTerminal : undefined,
          cashier_ids: filterSalesperson.length ? filterSalesperson : undefined,
          customer_ids: filterCustomer.length ? filterCustomer : undefined,
          status: filterStatus === 'all' ? undefined : filterStatus,
          ordering: sorting === 'DESC' ? '-estimate_no' : sorting === 'ASC' ? 'estimate_no' : undefined,
        };

        const resp = await EstimateService.getEstimateSummaryReport(params);
        setItems(resp);
        setFilteredItems(resp);
        setPrevFilterData({
          filterFromDate: start,
          filterToDate: end,
          sorting,
          filterSalesperson,
          filterTerminal,
          filterStatus,
          filterCustomer,
        });
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [
      filterFromDate,
      filterToDate,
      filterTerminal,
      filterSalesperson,
      filterCustomer,
      filterStatus,
      sorting,
      postAlertMessage,
    ]
  );

  const generateSummaryReport = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('ESTIMATE_SUMMARY');
    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);
    loadEstimateReports(dynamicStartDate, dynamicEndDate);
    setIsShowGenerateButton(false);
  }, [loadEstimateReports]);

  useEffect(() => {
    getCustomers();
    getSalespersons();
  }, [getCustomers, getSalespersons]);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  useEffect(() => {
    generateSummaryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (estimateToPrint && billFormatToPrint && estimateTotalsToPrint) {
      document.body.classList.add('printing-invoice');
      setTimeout(() => {
        window.print();
        document.body.classList.remove('printing-invoice');
        setEstimateToPrint(null);
        setBillFormatToPrint(null);
        setEstimateTotalsToPrint(null);
      }, 500);
    }
  }, [estimateToPrint, billFormatToPrint, estimateTotalsToPrint]);

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(
      items.filter(
        (item) =>
          item.estimate_no.toLowerCase().includes(value.toLowerCase()) ||
          (item.customer?.name && item.customer.name.toLowerCase().includes(value.toLowerCase()))
      )
    );
  };

  const handlePrintClick = async (estimateSummary) => {
    setLoadingIndicator(true);
    try {
      const [fullEstimate, billFormat] = await Promise.all([
        EstimateService.getEstimate(estimateSummary.id),
        getBillFormat(estimateSummary.initiated_terminal_id),
      ]);

      setEstimateToPrint(fullEstimate);
      setBillFormatToPrint(billFormat);

      const totals = fullEstimate.items.reduce(
        (accumulatedTotals, item) => {
          accumulatedTotals.payable += item.payable || 0;
          accumulatedTotals.tax += item.tax || 0;
          accumulatedTotals.cess += item.cess || 0;
          return accumulatedTotals;
        },
        { payable: 0, tax: 0, cess: 0 }
      );

      setEstimateTotalsToPrint(totals);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const _getEstimateReportSummary = (items) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    items.forEach((item) => {
      totalAmount += item.total || 0;
      totalTax += item.tax_amount || 0;
      totalAdditionalTax += item.cess_amount || 0;
    });

    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const handlePrintReport = () => {
    const summary = _getEstimateReportSummary(filteredItems);
    handlePrintReportHook(tCommon('Estimate Summary Report'), filterFromDate, filterToDate, headerData, filteredItems, {
      [tCommon('Tax Amount')]: summary.totalTax,
      [tCommon('Additional Tax')]: summary.totalAdditionalTax,
      [tCommon('Total Amount')]: summary.totalAmount,
    });
  };

  const headerData = [
    {
      label: tCommon('Estimate No'),
      id: 'estimate_no',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => (item.time ? DateTimeHelpers.formatServerTimeSegment(item.time) : ''),
    },
    {
      label: tCommon('Customer Name'),
      id: 'customer_name',
      type: 'callback',
      viewRender: (item) => item.customer_name || item.customer?.name || 'N/A',
    },
    {
      label: tCommon('Status'),
      id: 'status',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total || 0)}`;
      },
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (estimate) => (
        <Grid className={styles.btnGroup}>
          <Button
            variant="outlined"
            size="small"
            className={styles.convertBtn}
            onClick={() => EstimateHelper.convertToSale(history, estimate.id)}
          >
            {tCommon('convert_to_sale')}
          </Button>
          <Tooltip
            title={tCommon('Edit Estimate')}
            onClick={() => history.push(`${ROUTES.ESTIMATES}?id=${estimate.id}`)}
          >
            <EditIcon className={styles.iconBtn} />
          </Tooltip>
          <Tooltip title={tCommon('Print Estimate')} onClick={() => handlePrintClick(estimate)}>
            <PrintIcon className={styles.iconBtn} />
          </Tooltip>
        </Grid>
      ),
    },
  ];

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Estimates')}</span>
        </span>
        <div style={{ display: 'flex', justifyContent: 'flex-end', flex: 1, paddingRight: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Print Report')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={handlePrintReport}
            >
              <PrintIcon className={styles.actionBtnIcon} />
              {tCommon('Print')}
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Estimate Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by estimate no, customer')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="ESTIMATE_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminalMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminalsFromContext.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Customer')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterCustomer) => {
                    if (filterCustomer.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterCustomer.map((id) => customerMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder={tCommon('Search')}
                      fullWidth
                      InputProps={{
                        startAdornment: <SearchIcon style={{ color: 'grey' }} />,
                      }}
                      onChange={(e) => setCustomerSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                    />
                  </ListSubheader>
                  {customers &&
                    customers.length > 0 &&
                    customers
                      .filter((customer) => customer.name.toLowerCase().includes(customerSearchText.toLowerCase()))
                      .map((customer) => (
                        <MenuItem key={customer.id} value={customer.id}>
                          <Checkbox checked={filterCustomer.includes(customer.id)} color="primary" />
                          <ListItemText primary={customer?.name} />
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterSalesperson}
                  onChange={(e) => setFilterSalesperson(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterSalesperson) => {
                    if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterSalesperson.map((id) => salesPersonMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {salesPersons.map((salesperson) => (
                    <MenuItem key={salesperson.id} value={salesperson.id}>
                      <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                      <ListItemText primary={salesperson?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Status')}</label>
            </div>
            <div>
              <TextField
                select
                size="small"
                variant="outlined"
                style={{ width: 135 }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">
                  <em>{tCommon('All')}</em>
                </MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sort Order')}</label>
            </div>
            <div>
              <TextField
                select
                size="small"
                variant="outlined"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
              >
                <MenuItem value={'default'}>
                  <em>{tCommon('Default')}</em>
                </MenuItem>
                <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
              </TextField>
            </div>
          </div>
          {isShowGenerateButton && (
            <div className={styles.filterDiv}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Generate Report')}</label>
              </div>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.actionBtn}
                  style={{ backgroundColor: '#00a65a' }}
                  onClick={loadEstimateReports}
                  disabled={isShowGenerateButton === false || loadingIndicator}
                >
                  {loadingIndicator ? (
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                  ) : (
                    <SyncIcon className={styles.actionBtnIcon} />
                  )}
                  {tCommon('Generate')}
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.tableSec}>
          <DataTable columns={headerData} rows={filteredItems} isLoading={loadingIndicator} />
        </div>
      </div>
      {estimateToPrint &&
        createPortal(
          <PrintDocument
            type={PRINT_TYPES.ESTIMATE}
            data={estimateToPrint}
            shop={shop}
            billFormat={billFormatToPrint}
            totals={estimateTotalsToPrint}
          />,
          document.body
        )}

      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(EstimateReport);
